<script>
import render from 'posthtml-render'
import { omit, pathOr, pipe } from 'ramda'
import { mapGetters } from 'vuex'

import mixins from '@/utils/mixins'
import { PROP_TYPES, propValidator } from '~/utils/validators'
import AArticleTemplate from '@/components/_sections/article/AArticleTemplate'
import ABanner from 'shared/ABanner'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import ATerm from 'shared/ATerm'
import AAutomatedLink from '@/components/_sections/article/AAutomatedLink'
import ATableInsideArticle from '@/components/_sections/article/ATableInsideArticle'
import { TABLE_SHORTCODE } from 'sections/article/ATableInsideArticle/enums'
import { PAGE_SCHEME_TYPE } from 'enums/pageSchemes'
import { ROUTE, ROUTE_NAME } from 'enums/routes'
import { SPECIAL_CATEGORY, SPECIAL_SUBCATEGORY } from 'enums/special-categories'
import { REFS } from 'enums/external-refs'
import { IMAGE_SIZE } from 'enums/images'
import ArticleVideoBanner from './ArticleVideoBanner'
import { joinStringsWithSpaces } from '@/plugins/helper'
import { addClassToParentElements } from '@/utils/helpers/processHtml/addClassToParentElements'
import { processHtml } from '@/utils/helpers/processHtml'
import {
  addTwitterCardScript,
  normalizeTwitterCardScriptSource
} from '@/utils/helpers/processHtml/twitterCard'
import { rawDateUTCStringToPageSchemeFormat } from '@fmpedia/helpers'
import { COOKIE_ENTITY_TYPE } from 'enums/cmp'
import { FIXED_LAYOUT } from 'enums/fixed-layout'
import { parseHtml } from '@/utils/helpers/processHtml/parseHtml'
import {
  checkIfParsedElementIsTerm,
  getTermIdFromParsedTerm
} from '@/utils/helpers/processHtml/processTerms'
import { HTML_TAG } from '@/utils/helpers/processHtml/enums'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { SRCSET_LOCATION, getImagePreloadLink } from '@/utils/enums/images'
import { decodeHTML } from 'entities'
import { CUSTOM_HTML_TAG, MEDIA_ID_DATA_ATTRIBUTE } from '@fmpedia/enums'
import { MODAL } from 'modals/AModalWrapper'
import AEnlargeableImage from 'shared/AEnlargeableImage'
import { RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME } from 'enums/article'
import AHighlightedLinksInsideArticle from 'sections/article/AHighlightedLinksInsideArticle'
import { HIGHLIGHTED_LINKS_SHORTCODE } from 'sections/article/AHighlightedLinksInsideArticle/enums'
import { encodeAttributeQuotes } from '@/utils/helpers/encodeDecodeAttributeQuotes'
import { addOneSignalInterestTag } from '@/utils/helpers/one-signal-interest-tags'

function wrapIntoLazyHydrate(component) {
  return {
    tag: 'a-lazy-hydrate',
    props: {
      'when-visible': true
    },
    content: [component]
  }
}

const INTERRUPTIVE_ELEMENTS = {
  AUTOMATED_LINKS: 'AutomatedLinks',
  BANNERS: 'Banners',
  EBOOK_PLACEHOLDER: 'EbookPlaceholder',
  EBOOK: 'Ebook',
  VIDEO_BANNER: 'VideoBanner',
  RELATED_ARTICLES: 'RelatedArticles',
  DIRECTORY: 'Directory',
  OFFER: 'Offer'
}

/**
 Model:
{
  criteria: string ('tag', 'position')
  tag: Array of string (['p', 'h'])
  tagCount: Array of numbers ([2, 8] - means before/after 2nd and 8th tag)
  position: string ('before', 'after', 'start', 'end' - where to insert interruptive element)
  element: string ('Related', 'Banner', 'AutomatedLinks', 'Ebook', 'VideoBanner', 'Directory', 'Offer')
  beforeExceptions: Array of strings (['h'] - do not insert if previous tag is header)
  afterExceptions: Array of strings (['video'] - do not insert if next tag is video)
}
**/

const interruptiveRulesDesktop = [
  {
    criteria: 'tag',
    tag: ['p'],
    tagCount: [1],
    shiftAllowed: Infinity,
    position: 'after',
    element: INTERRUPTIVE_ELEMENTS.AUTOMATED_LINKS,
    beforeException: [],
    afterException: []
  }
]

const interruptiveRulesMobile = [
  {
    criteria: 'tag',
    tag: ['p'],
    tagCount: [2],
    shiftAllowed: 1,
    position: 'after',
    element: INTERRUPTIVE_ELEMENTS.AUTOMATED_LINKS,
    beforeException: [],
    afterException: ['image', 'video']
  }
]

const blankInterruptiveElements = Object.values(INTERRUPTIVE_ELEMENTS).reduce(
  (acc, element) => ({ ...acc, [element]: [] }),
  []
)

const PARENT_ELEMENT_CLASS = 'article-body__parent-element'

class InterruptiveElements {
  constructor({
    sourceHtml,
    rules,
    interruptiveElements,
    article,
    onEnlargeImage
  }) {
    this.article = article
    this.sourceHtml = sourceHtml
    this.rules = rules
    this.interruptiveElements = interruptiveElements
    this.ieIndex = Object.keys(interruptiveElements).reduce((acc, ieKey) => {
      return {
        ...acc,
        [ieKey]: 0
      }
    }, {})
    this.count = {
      h: 0,
      p: 0,
      video: 0,
      image: 0
    }
    this.currentTag = ''
    this.previousTag = ''
    this.nextTag = ''
    this.parsedHtml = []
    this.parsedResult = []
    this.resultHtml = ''
    this.loggedIE = {}
    this.clearfix = {
      tag: 'div',
      class: 'clearfix'
    }
    this.ieCounts = Object.keys(interruptiveElements).reduce(
      (acc, element) => ({ ...acc, [element]: 0 }),
      {}
    )
    this.onEnlargeImage = onEnlargeImage
  }
  getDeepProp(path, object) {
    return path.reduce((xs, x) => (xs && xs[x] ? xs[x] : null), object)
  }
  parse() {
    const tags = parseHtml(this.sourceHtml.replace(/&nbsp;/gi, ' '))
    this.parsedHtml = this.processHtmlStructure(tags).map(tag =>
      this.parseTag(tag)
    )
    return this
  }
  addClassToParentElements() {
    this.parsedHtml = addClassToParentElements(
      this.parsedHtml,
      PARENT_ELEMENT_CLASS
    )
    return this
  }
  /**
   * Here we can process all tags before main processing.
   * Now we don't use it as we have prettifyHtml helper function
   **/
  processHtmlStructure(tags) {
    return tags
    /* return tags.reduce((acc, tag) => {
      const resultEl = this.processHtmlFigure(tag)
      return [...acc, ...resultEl]
    }, []) */
  }
  isLink(el) {
    return el && el.tag === HTML_TAG.LINK && !checkIfParsedElementIsTerm(el)
  }
  isTable({ tag = '' } = {}) {
    return tag === 'table'
  }
  isFigure({ tag = '' } = {}) {
    return tag === HTML_TAG.FIGURE
  }
  parseTag(elem) {
    if (!elem) return elem

    function enlarge() {
      this.onEnlargeImage(elem)
    }

    if (checkIfParsedElementIsTerm(elem)) {
      const elementId = getTermIdFromParsedTerm(elem)
      const { name, slug, description } =
        this.article.terms.find(({ id }) => id === elementId) || {}
      const content = render(elem.content)

      return wrapIntoLazyHydrate({
        ...elem,
        tag: 'a-term',
        attrs: {
          content,
          name,
          description,
          slug
        }
      })
    } else if (this.isLink(elem)) {
      return {
        ...elem,
        class: 'article-link'
      }
    } else if (this.isTable(elem)) {
      return {
        ...elem,
        content: elem.content.map(item => this.parseTag(item)),
        class: 'jodit-table'
      }
    } else if (this.isFigure(elem)) {
      const enlargeableIconEl = elem.content.find(child => {
        return (
          child &&
          child.tag &&
          child.tag === CUSTOM_HTML_TAG.A_ENLARGEABLE_IMAGE
        )
      })

      if (!enlargeableIconEl) return elem

      enlargeableIconEl.on = {
        enlarge: enlarge.bind(this)
      }
      return elem
    } else if (elem.content) {
      return {
        ...elem,
        content: elem.content.map(item => this.parseTag(item))
      }
    }

    return elem
  }
  findNestedTag(tagObject, tag) {
    let result = false

    function findChildTag(tagObject, tag) {
      if (result) {
        return
      }
      if (tagObject.tag && tagObject.tag === tag) {
        result = true
        return
      }
      if (tagObject.content) {
        tagObject.content.map(child => {
          findChildTag(child, tag)
        })
      }
    }
    findChildTag(tagObject, tag)
    return result
  }
  recognizeTag(tagObject) {
    if (!tagObject || !tagObject.tag) {
      return ''
    }
    switch (tagObject.tag) {
      case 'p':
        if (tagObject.content) {
          return 'p'
        }
        return ''
      case 'h2':
        return 'h'
      case 'figure':
        if (this.findNestedTag(tagObject, 'iframe')) {
          return 'video'
        } else if (this.findNestedTag(tagObject, 'img')) {
          return 'image'
        }
        return ''
      case 'blockquote':
        return 'blockquote'
      default:
        return ''
    }
  }
  getInterruptiveElementData(elementName) {
    return {
      elementName,
      parsedHtmlObject: this.interruptiveElements[elementName][
        this.ieIndex[elementName]
      ]
    }
  }
  isIEExist({ parsedHtmlObject }) {
    return !!parsedHtmlObject
  }
  processStartPosition() {
    this.rules
      .filter(rule => rule.criteria === 'position' && rule.position === 'start')
      .map(rule => {
        const noIEBeforeFirstTag = !this.getDeepProp(
          [1, 'before'],
          this.loggedIE
        )
        const intElement = this.getInterruptiveElementData(rule.element)
        if (noIEBeforeFirstTag && this.isIEExist(intElement)) {
          this.logIE(0, 'before', rule.element)
          this.ieIndex[rule.element]++
          this.parsedResult.push(this.clearfix)
          this.addInterruptiveElement(intElement)
        }
      })
  }
  processEndPosition() {
    this.rules
      .filter(rule => rule.criteria === 'position' && rule.position === 'end')
      .map(rule => {
        /** ToDo: We are allowed to add multiple interruptive elements at the end of the
         * article in Phase 1
         */
        const noIEAfterSameTag = true
        // const noIEAfterSameTag = !this.getDeepProp(
        //   [this.count[this.currentTag], 'after'],
        //   this.loggedIE
        // )
        const intElement = this.getInterruptiveElementData(rule.element)
        if (noIEAfterSameTag && this.isIEExist(intElement)) {
          this.logIE(this.count[this.currentTag], 'after', rule.element)
          this.ieIndex[rule.element]++
          this.parsedResult.push(this.clearfix)
          this.addInterruptiveElement(intElement)
        }
      })
  }
  processBeforeTag() {
    this.rules
      .filter(
        rule =>
          rule.criteria === 'tag' &&
          rule.position === 'before' &&
          rule.tag.includes(this.currentTag)
      )
      .map(rule =>
        rule.tagCount.map(tagPosition => {
          const sameTagPosition = tagPosition === this.count[rule.tag]
          const shiftedTagPosition =
            this.count[rule.tag] - tagPosition >= 0 &&
            this.count[rule.tag] - tagPosition <= rule.shiftAllowed
          const noExceptionsBefore = !rule.beforeException.includes(
            this.previousTag
          )
          const noExceptionsAfter = !rule.afterException.includes(this.nextTag)
          const noIEBeforeSameTag = !this.getDeepProp(
            [this.count[rule.tag], 'before'],
            this.loggedIE
          )
          // const noIEAfterPreviousTag = !this.getDeepProp(
          //   [this.count[rule.tag] - 1, 'after'],
          //   this.loggedIE
          // )
          const notAddedAlready = !(
            this.getDeepProp([tagPosition, 'before'], this.loggedIE) ===
            rule.element
          )
          const intElement = this.getInterruptiveElementData(rule.element)
          if (
            (sameTagPosition || shiftedTagPosition) &&
            noExceptionsBefore &&
            noExceptionsAfter &&
            noIEBeforeSameTag &&
            notAddedAlready &&
            this.isIEExist(intElement)
          ) {
            this.logIE(tagPosition, 'before', rule.element)
            this.ieIndex[rule.element]++
            this.parsedResult.push(this.clearfix)
            this.addInterruptiveElement(intElement)
          }
        })
      )
  }
  processAfterTag() {
    this.rules
      .filter(
        rule =>
          rule.criteria === 'tag' &&
          rule.position === 'after' &&
          rule.tag.includes(this.currentTag)
      )
      .map(rule => {
        rule.tagCount.map(tagPosition => {
          const sameTagPosition = tagPosition === this.count[rule.tag]
          const shiftedTagPosition =
            this.count[rule.tag] - tagPosition >= 0 &&
            this.count[rule.tag] - tagPosition <= rule.shiftAllowed
          const noExceptionsBefore = !rule.beforeException.includes(
            this.previousTag
          )
          const noExceptionsAfter = !rule.afterException.includes(this.nextTag)
          const noIEAfterSameTag = !this.getDeepProp(
            [this.count[rule.tag], 'after'],
            this.loggedIE
          )
          // const noIEBeforeNextTag = !this.getDeepProp(
          //   [this.count[rule.tag] + 1, 'before'],
          //   this.loggedIE
          // )
          const notAddedAlready = !(
            this.getDeepProp([tagPosition, 'after'], this.loggedIE) ===
            rule.element
          )
          const intElement = this.getInterruptiveElementData(rule.element)
          if (
            (sameTagPosition || shiftedTagPosition) &&
            noExceptionsBefore &&
            noExceptionsAfter &&
            noIEAfterSameTag &&
            notAddedAlready &&
            this.isIEExist(intElement)
          ) {
            this.logIE(tagPosition, 'after', rule.element)
            this.ieIndex[rule.element]++
            this.parsedResult.push(this.clearfix)
            this.addInterruptiveElement(intElement)
          }
        })
      })
  }
  logIE(tagNumber, position, element) {
    if (this.loggedIE[tagNumber] === undefined) {
      this.loggedIE[tagNumber] = {}
    }
    this.loggedIE[tagNumber][position] = element
  }
  getPreviousTag(currentIndex) {
    if (currentIndex > 0) {
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (this.parsedHtml[i].tag) {
          return this.recognizeTag(this.parsedHtml[i])
        }
      }
    }
    return ''
  }
  getNextTag(currentIndex) {
    if (currentIndex < this.parsedHtml.length - 1) {
      for (let i = currentIndex + 1; i < this.parsedHtml.length; i++) {
        if (this.parsedHtml[i].tag) {
          return this.recognizeTag(this.parsedHtml[i])
        }
      }
    }
    return ''
  }
  applyRules() {
    this.parsedHtml.map((element, index) => {
      if (index === 0) {
        this.processStartPosition()
      }
      this.currentTag = this.recognizeTag(element)
      if (this.currentTag) {
        this.previousTag = this.getPreviousTag(index)
        this.nextTag = this.getNextTag(index)
        this.count[this.currentTag]++
        this.addClearfixBeforeImage(this.currentTag)
        this.addClearfixBeforeBlockquote(this.currentTag)
        this.processBeforeTag()
        this.parsedResult.push(element)
        this.processAfterTag()
        if (index === this.parsedHtml.length - 1) {
          this.processEndPosition()
        }
      } else {
        this.parsedResult.push(element)
      }
      if (index === this.parsedHtml.length - 1) {
        this.processEndPosition()
      }
    })
    return this
  }
  addInterruptiveElement({ parsedHtmlObject, elementName } = {}) {
    if (!parsedHtmlObject) return

    this.ieCounts[elementName]++
    this.parsedResult.push(parsedHtmlObject)
  }
  addClearfixBeforeImage(tag) {
    if (tag === 'image') {
      this.parsedResult.push(this.clearfix)
    }
  }
  addClearfixBeforeBlockquote(tag) {
    if (tag === 'blockquote') {
      this.parsedResult.push(this.clearfix)
    }
  }
  renderToHtml() {
    this.resultHtml = render(this.parsedResult)
    return this
  }
  createVueElement(h, vNode = this.parsedResult) {
    if (vNode.length === 0) {
      return []
    }
    const children = []
    vNode.forEach(element => {
      if (element === undefined) return

      if (typeof element === 'string') {
        children.push(decodeHTML(element))
        return
      }

      const attrs = element.attrs
        ? { attrs: omit(['class'], element.attrs) }
        : {}
      const attrsClass = (element.attrs && element.attrs.class) || ''
      const elementClass = element.class || ''
      const mergedClass = joinStringsWithSpaces([attrsClass, elementClass])

      const attributes = {
        props: element.props,
        ...attrs,
        class: mergedClass,
        style: pathOr(null, ['attrs', 'style'], element),
        on: element.on
      }

      const nestedChildren =
        element.content && element.content.length
          ? this.createVueElement(h, element.content)
          : null

      children.push(h(element.tag, attributes, nestedChildren))
    })
    return children
  }
  getResults(h) {
    return {
      vueElement: this.createVueElement(h),
      htmlElementCount: this.count,
      interruptiveElementCount: this.ieCounts
    }
  }
  get getHtml() {
    return this.resultHtml
  }
}

const ARTICLE_BANNERS = [
  {
    tag: 'a-banner',
    props: {
      bannerSettings: BANNER_SETTINGS.ARTICLE.MPU_BANNER_INSIDE_1_ARTICLE
    },
    class: 'article__mpu-banner'
  }
]

const SCROLL_TO_OFFSET_DESKTOP = {
  offer: -50,
  ebook: -55
}

const SCROLL_TO_OFFSET_MOBILE = {
  offer: -110,
  ebook: -115
}

const QIR_SHORTCODE_REGEXP_STRING = '\\[qir_table_form [^\\]]+?\\]'
const QIR_SHORTCODE_DUPLICATE_REGEXP = new RegExp(
  `(${QIR_SHORTCODE_REGEXP_STRING}){2,}`,
  'gi'
)
const QIR_SHORTCODE_REPLCACE_REGEXP = new RegExp(
  QIR_SHORTCODE_REGEXP_STRING,
  'gi'
)

export default {
  name: 'ArticleComponent',
  template: '<div>ArticleContent</div>',
  mixins: [
    mixins.initialScroll,
    mixins.urlGenerators,
    mixins.cmp,
    mixins.disqusCommentCountMixinFactory(),
    hydrationHelpers
  ],
  head() {
    const {
      title,
      brief,
      featuredVideo,
      featuredImage,
      bodyVideos,
      canonicalUrl
    } = this.articleData

    const heroPreloadLink =
      !featuredVideo &&
      getImagePreloadLink({
        url: featuredImage?.url,
        location: SRCSET_LOCATION.ARTICLE_PAGE
      })

    const {
      AuthorArticles,
      CategoryArticles,
      EducationArticles,
      KeepReadingArticles,
      LatestArticles,
      MostViewedArticles,
      TrendingArticles
    } = this.relatedArticles

    return this.$generateMetaTags({
      titleParam: title,
      descriptionParam: brief,
      schemes: [
        this.generateArticleScheme(),
        ...this.$helper.generateVideoSchemasFromResponse([
          featuredVideo,
          ...(bodyVideos || []),
          ...this.relatedVideos.map(videoData => videoData.Video),
          ...AuthorArticles.map(article => article?.featuredVideo),
          ...CategoryArticles.map(article => article?.featuredVideo),
          ...EducationArticles.map(article => article?.featuredVideo),
          ...KeepReadingArticles.map(article => article?.featuredVideo),
          ...LatestArticles.map(article => article?.featuredVideo),
          ...MostViewedArticles.map(article => article?.featuredVideo),
          ...TrendingArticles.map(article => article?.featuredVideo)
        ])
      ],
      canonicalUrlGenerator: () => this.articleData.canonicalUrl,
      ogImageUrl: this.thumbnailUrl,
      additionalLinks: heroPreloadLink ? [heroPreloadLink] : [],
      canonicalUrl
    })
  },
  components: {
    AArticleTemplate,
    ABanner,
    AAutomatedLink,
    ATerm,
    ATableInsideArticle,
    ArticleVideoBanner,
    AEnlargeableImage,
    AHighlightedLinksInsideArticle
  },
  props: {
    articleData: propValidator([PROP_TYPES.OBJECT], false, () => ({})),
    relatedArticles: propValidator([PROP_TYPES.OBJECT]),
    relatedVideos: propValidator([PROP_TYPES.ARRAY]),
    articleSlug: propValidator([PROP_TYPES.STRING]),
    categorySlug: propValidator([PROP_TYPES.STRING]),
    subcategorySlug: propValidator([PROP_TYPES.STRING], false),
    isEbookLoaded: propValidator([PROP_TYPES.BOOLEAN], false, false),
    showDisclaimer: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  data() {
    return {
      mobileElement: null,
      desktopElement: null,
      onSetPopupImage: null,
      forceRegenerateElements: false
    }
  },
  computed: {
    ...mapGetters({
      checkIfThoughtLeadershipSlug: 'categories/checkIfThoughtLeadershipSlug'
    }),
    thumbnailUrl() {
      const { featuredImage } = this.articleData
      const featuredImageUrl = pathOr(null, ['url'], featuredImage)

      return featuredImageUrl
        ? /** Due to the Google recommendations, Images should be at least 696 pixels wide,
           * that's why we take the nearest appropriate size - large - 900px **/
          this.$helper.replaceImageUrlSuffix({
            originalUrl: featuredImageUrl,
            neededSize: IMAGE_SIZE.WIDTH_900
          })
        : this.$helper.getLinkToFmBucketFile(
            'article-default-image-small-180-100.jpg'
          )
    },
    srcsetLocation() {
      return SRCSET_LOCATION.ARTICLE_PAGE
    },
    processedBody() {
      const processedBody = pipe(
        this.setTableInsideArticle,
        this.setHighlightedLinksInsideArticle,
        this.handleIntelligenceShortCodes,
        normalizeTwitterCardScriptSource,
        addTwitterCardScript
      )(this.articleData.body)

      return processHtml(processedBody, {
        allowScripts: true,
        imageCustomComponent: CUSTOM_HTML_TAG.A_ENLARGEABLE_IMAGE,
        srcsetLocation: this.srcsetLocation,
        addImageSizeClass: true
      })
    },
    isThoughtLeadershipArticle() {
      return this.checkIfThoughtLeadershipSlug(this.articleData.categorySlug)
    },
    isCurrentRouteNewsArticle() {
      const categoryLowerCased = (this.categorySlug || '').toLowerCase()
      const subcategoryLowerCased = (this.subcategorySlug || '').toLowerCase()

      const isCurrentRouteEducationCenter =
        this.$route.name === ROUTE_NAME.CATEGORY_EDUCATION_CENTER_ARTICLE
      const isCurrentCategorySpecial =
        categoryLowerCased === SPECIAL_CATEGORY.THOUGHT_LEADERSHIP
      const isCurrentArticleExecutivesInterview =
        categoryLowerCased === SPECIAL_CATEGORY.EXECUTIVES &&
        subcategoryLowerCased === SPECIAL_SUBCATEGORY.INTERVIEW

      return (
        !isCurrentRouteEducationCenter &&
        !isCurrentCategorySpecial &&
        !isCurrentArticleExecutivesInterview
      )
    }
  },
  watch: {
    'articleData.id'() {
      this.trackOneSignalInterestTag()
    }
  },
  methods: {
    trackOneSignalInterestTag() {
      if (process.server || this.$route.query.previewId || !this.articleData) {
        return
      }

      if (!this.$_cmp_isConsentGivenForEntity(COOKIE_ENTITY_TYPE.ONE_SIGNAL)) {
        return
      }

      addOneSignalInterestTag({
        categorySlug: this.articleData.categorySlug,
        subcategorySlug: this.articleData.subcategorySlug,
        contentItemId: this.articleData.id
      })
    },
    setBanners(interruptiveElements) {
      return {
        ...interruptiveElements,
        Banners: ARTICLE_BANNERS
      }
    },
    setAutomatedLink(interruptiveElements) {
      if (this.articleData.automaticLink) {
        return {
          ...interruptiveElements,
          AutomatedLinks: [
            wrapIntoLazyHydrate({
              tag: 'a-automated-link',
              props: {
                linkDetails: this.articleData.automaticLink
              }
            })
          ]
        }
      }
      return interruptiveElements
    },
    setVideoBanner(layout) {
      return interruptiveElements => {
        const articleVideoBanner = {
          tag: 'article-video-banner',
          props: {
            layout
          }
        }

        return {
          ...interruptiveElements,
          VideoBanner: [articleVideoBanner]
        }
      }
    },
    setTableInsideArticle(body) {
      if (!body.includes(TABLE_SHORTCODE)) return body

      const tableData = encodeAttributeQuotes(this.articleData.tableData)
      const tableTag = `<a-table-inside-article tableData='${tableData}'></a-table-inside-article>`

      return body.replace(TABLE_SHORTCODE, tableTag)
    },
    setHighlightedLinksInsideArticle(body) {
      if (!body.includes(HIGHLIGHTED_LINKS_SHORTCODE)) return body

      const linksDataStringified = encodeAttributeQuotes(
        JSON.stringify(this.articleData.highlightedArticles)
      )

      const tag = `<a-highlighted-links-inside-article linksData='${linksDataStringified}'></a-highlighted-links-inside-article>`

      return body.replace(HIGHLIGHTED_LINKS_SHORTCODE, tag)
    },
    handleIntelligenceShortCodes(body) {
      const linkToIntelligenceProducts = this.$helper.addEndingSlash(
        `${this.$env.DOMAIN_URL}${ROUTE.INTELLIGENCE_PRODUCTS}`
      )
      const htmlLink = `<a href="${linkToIntelligenceProducts}">Intelligence Products | Finance Magnates</a>`

      return body
        .replace(QIR_SHORTCODE_DUPLICATE_REGEXP, '$1')
        .replace(QIR_SHORTCODE_REPLCACE_REGEXP, htmlLink)
    },
    schemaAuthorFromPublisher(publisher) {
      if (!publisher?.name) return null

      if (publisher.slug) {
        return {
          '@type': 'Person',
          name: publisher.name,
          ...(publisher.link ? { url: this.generateFmUrl(publisher.link) } : {})
        }
      }

      return {
        '@type': 'Organization',
        name: publisher.name,
        ...(publisher.link ? { url: publisher.link } : {})
      }
    },
    schemaAuthor() {
      const authors = (this.articleData.publishers || [])
        .map(publisher => this.schemaAuthorFromPublisher(publisher))
        .filter(Boolean)

      if (!authors.length) return undefined
      return authors.length === 1 ? authors[0] : authors
    },
    generateArticleScheme() {
      const {
        title,
        publishedOn,
        modifiedOn,
        brief,
        categoryName,
        subcategoryName
      } = this.articleData

      const fullPath = this.$helper.getFullPath(this)
      const dateline = this.$helper.formatDate.toDateAndHours(publishedOn)
      const bodyText = this.$helper.htmlToText(this.processedBody)
      const articleSection = `${categoryName} ${subcategoryName}`
      const wordCount = this.$helper.countWordsAmount(bodyText)

      const { Keyphrases } = this.$store.getters['seo/pageSeo']

      const publishedOnFormatted = rawDateUTCStringToPageSchemeFormat(
        publishedOn
      )
      const modifiedOnFormatted = rawDateUTCStringToPageSchemeFormat(modifiedOn)

      const commonFields = {
        articleBody: bodyText,
        articleSection,
        wordCount,
        alternativeHeadline: title,
        datePublished: publishedOnFormatted,
        dateModified: modifiedOnFormatted || publishedOnFormatted,
        headline: title,
        thumbnailUrl: this.thumbnailUrl,
        description: brief,
        author: this.schemaAuthor(),
        mainEntityOfPage: this.articleData.canonicalUrl
      }

      const specificSchemeFields = this.isCurrentRouteNewsArticle
        ? {
            dateline,
            genre: articleSection,
            text: fullPath
          }
        : {
            discussionUrl: fullPath,
            keywords: Keyphrases.map(({ Keyword }) => Keyword).join(', '),
            timeRequired: this.$helper.calculateTimeRequiredToRead({
              wordCount
            }),
            alternateName: title
          }

      const type = this.isCurrentRouteNewsArticle
        ? PAGE_SCHEME_TYPE.NEWS_ARTICLE
        : PAGE_SCHEME_TYPE.ARTICLE

      return { type, data: { ...commonFields, ...specificSchemeFields } }
    },
    onEnlargeImage(elem) {
      const img = elem.content.find(
        ({ tag }) => tag === CUSTOM_HTML_TAG.A_ENLARGEABLE_IMAGE
      )
      const payload = {
        MediaId: elem.attrs[MEDIA_ID_DATA_ATTRIBUTE],
        MediaType: 'Image',
        URL: img.attrs['data-src']
      }
      if (this.onSetPopupImage) {
        this.onSetPopupImage(payload)
        this.$nextTick(() => this.$helper.openModal(MODAL.IMAGE_DOWNLOAD))
      }
    },
    generateElements(h) {
      const isAlreadyGenerated =
        !this.forceRegenerateElements &&
        this.mobileElement &&
        this.desktopElement

      if (isAlreadyGenerated) return

      if (this.forceRegenerateElements) {
        this.forceRegenerateElements = false
      }

      const interruptiveElements = pipe(
        this.setBanners,
        this.setAutomatedLink
      )(blankInterruptiveElements)

      const interruptiveElementsDesktop = pipe(
        this.setVideoBanner(FIXED_LAYOUT.DESKTOP)
      )(interruptiveElements)

      const interruptiveElementsMobile = pipe(
        this.setVideoBanner(FIXED_LAYOUT.MOBILE)
      )(interruptiveElements)

      const articleWithIEDesktop = new InterruptiveElements({
        sourceHtml: this.processedBody,
        interruptiveElements: interruptiveElementsDesktop,
        rules: interruptiveRulesDesktop,
        article: this.articleData,
        onEnlargeImage: this.onEnlargeImage.bind(this)
      })
      const articleWithIEMobile = new InterruptiveElements({
        sourceHtml: this.processedBody,
        interruptiveElements: interruptiveElementsMobile,
        rules: interruptiveRulesMobile,
        article: this.articleData,
        onEnlargeImage: this.onEnlargeImage.bind(this)
      })

      const { vueElement: articleContentDesktop } = articleWithIEDesktop
        .parse()
        .addClassToParentElements()
        .applyRules()
        .getResults(h)

      const { vueElement: articleContentMobile } = articleWithIEMobile
        .parse()
        .addClassToParentElements()
        .applyRules()
        .getResults(h)

      this.desktopElement = h(
        'a-visibility',
        {
          props: {
            hide: true,
            on: [this.$breakpoint.mobile],
            keepOnClient: false
          },
          class: 'article-body__a-visibility'
        },
        [h('div', articleContentDesktop)]
      )
      this.mobileElement = h(
        'a-visibility',
        {
          props: {
            show: true,
            on: [this.$breakpoint.mobile],
            keepOnClient: false
          },
          class: 'article-body__a-visibility'
        },
        [h('div', articleContentMobile)]
      )
    },
    regenerateElements() {
      this.forceRegenerateElements = true
    }
  },
  render(h) {
    this.generateElements(h)

    const articleClass = [
      'html-content__article-styles',
      'html-content__article-font-styles'
    ].join(' ')

    const renderedContent = h(
      AArticleTemplate,
      {
        props: {
          article: this.articleData,
          relatedArticles: this.relatedArticles,
          relatedVideos: this.relatedVideos,
          articleSlug: this.articleSlug,
          categorySlug: this.categorySlug,
          subcategorySlug: this.subcategorySlug,
          isEbookLoaded: this.isEbookLoaded,
          showDisclaimer: this.showDisclaimer
        },
        on: this.$listeners
      },
      [
        ({ onSetPopupImage }) => {
          this.onSetPopupImage = onSetPopupImage
          return h(
            'article',
            {
              class: articleClass,
              attrs: { 'data-ref': REFS.CONTENT_BODY }
            },
            [
              this.desktopElement,
              this.mobileElement,
              h('div', { class: 'clearfix' })
            ]
          )
        }
      ]
    )

    return renderedContent
  },
  beforeMount() {
    /**
     * We have an optimization in "generateElements" method
     * that prevents calling some heavyweight operations multiple times.
     * However, as a side effect, when component props (such as articleData)
     * change - we need to somehow "turn off" this optimization. This is
     * done by leveraging the forceRegenerateElements flag.
     */
    this.$bus.$on(
      RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME,
      this.regenerateElements
    )
  },
  async mounted() {
    /** Send event only in case of:
     *  production && it is not article preview mode && it is not thought-leadership article **/
    if (
      process.env.NODE_ENV === 'production' &&
      !this.$route.params.previewId &&
      !this.isThoughtLeadershipArticle &&
      this.$_cmp_isConsentGivenForEntity(COOKIE_ENTITY_TYPE.GOOGLE_ANALYTICS)
    ) {
      /* Timeout is placed here because in a few cases GA sent wrong path (from
       * previous page). On 'nextTick' same issue.
       */
      setTimeout(() => {
        window.dataLayer.push({
          event: 'Newsletter Article'
        })
      })
    }

    await this.$nextTick()

    const { scrollTo } = this.$route.query
    if (scrollTo) {
      const offset = this.$_hydrationHelpers_mobileOrDesktopValue(
        SCROLL_TO_OFFSET_MOBILE,
        SCROLL_TO_OFFSET_DESKTOP
      )[scrollTo]
      this.$scrollTo(`#${scrollTo}`, 200, { offset })
    }

    this.trackOneSignalInterestTag()
  },
  beforeDestroy() {
    this.$bus.$off(
      RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME,
      this.regenerateElements
    )
  }
}
</script>

<style lang="scss" scoped>
@import '~assets/scss/local/styles/_html.scss';

.article {
  .article__labels-container {
    display: flex;
    flex-wrap: wrap;
    margin: 10px 0;
  }
}

.article__mpu-banner {
  float: left;
  clear: left;

  // If text node was inserted instead of a <p> tag through Jodit, then there will be no margin
  // between MPU banner and the article content. However, this is an invalid case, so we don't
  // handle it
  margin: 10px 30px 30px 0;
  width: 300px;
  height: 250px;

  @include mobile {
    width: 100%;
    height: auto;
    margin-right: 0;
    margin-top: 15px;
  }
}

.clearfix {
  @include clearfix;
}
</style>
<style lang="scss">
.twitter-tweet iframe {
  margin-bottom: 10px;
}

.article__banner-wrapper {
  display: flex;
  justify-content: center;
  margin: 30px -5px;
}
</style>
